import {
  IncomingEmailMessageModel,
  IssueEventModel,
  IssueLocationType,
  IssueModel,
} from 'shared/domain/issue/issueModel';
import { entityToModelBase } from 'shared/domain/toBaseModel';
import { toOptionalUserWithPersonalData } from 'shared/domain/user/mapping/toView';
import { UserEntity } from 'shared/domain/user/types/entity';
import { toReferenceId } from 'shared/utils/id/referenceId';
import { findById } from 'shared/utils/id/findById';
import { toAttachmentCounts } from 'shared/domain/report/estimate/classifyDocumentsForPdfEstimate';
import {
  EventEntity,
  IncomingEmailMessageEntity,
  IssueEntity,
} from './entity';

export function issueEntityToModel(
  entity: IssueEntity,
  users: UserEntity[]
): IssueModel {
  const modelBase = entityToModelBase({ entity, users });
  const processId = toReferenceId(entity.process);
  const siteId = toReferenceId(entity.primaryData.site);
  const levelId = toReferenceId(entity.primaryData.level);

  return {
    ...modelBase,
    inspection: entity.inspection,
    protocolItem: entity.protocolItem,
    process: processId,
    stageId: entity.stageId,
    workflowId: entity.workflowId,
    userAccesses: entity.userAccesses,
    primaryData: {
      assignee: toOptionalUserWithPersonalData(
        findById(users, entity.primaryData.assignee, 'issue assignee')
      ),
      contractNumbers: entity.primaryData.contractNumbers ?? [],
      description: entity.primaryData.description,
      detectionDate: entity.primaryData.detectionDate,
      level: levelId,
      positionOnMap: entity.primaryData.positionOnMap,
      site: siteId,
      subcontractors: entity.primaryData.subcontractors ?? [],
      title: entity.primaryData.title,
      executor: toOptionalUserWithPersonalData(
        findById(users, entity.primaryData.executor, 'issue executor')
      ),
      selectedLocationType:
        entity.primaryData.selectedLocationType || IssueLocationType.pin,
      targetAreas: entity.primaryData.targetAreas ?? [],
      finalAreas: entity.primaryData.finalAreas ?? [],
    },
    hashtag: entity._id ? entity._id.slice(-4) : '',
    inspectionNumberSearch: entity.inspectionNumberSearch,
    inspectionNumberSearchNormalized:
      entity.inspectionNumberSearchNormalized,
    events: entity.events.map((event) =>
      issueEventEntityToModel(event, users)
    ),
    workflowDecisions: entity.workflowDecisions ?? [],
    // Do NOT coerce to `null`: these are only computed by `getFiltered()`
    // (see `addLastDecisionStatusField`). Entities loaded through other paths
    // (`getOne`, `getMany`, broadcasts, etc.) never set these fields, so they
    // must stay `undefined` ("not computed") rather than be misreported as
    // `null` ("N/A", a distinct, meaningful domain value).
    lastDecisionStatus: entity.lastDecisionStatus,
    latestDecisionId: entity.latestDecisionId,
    incomingEmailMessages:
      entity.incomingEmailMessages?.map((message) =>
        incomingEmailMessageEntityToModel(message, users)
      ) ?? [],
    extendedData: {
      ...entity.extendedData,
    },
    mainImage: entity.mainImage,
    emailAddress: entity.emailAddress,
    linkedDocumentation: (entity.linkedDocumentation ?? []).filter(item => !item.deleted),
  };
}

function issueEventEntityToModel(
  issueEventEntity: EventEntity,
  users: UserEntity[]
): IssueEventModel {
  const { description, title, type } = issueEventEntity;
  const modelBase = entityToModelBase({ entity: issueEventEntity, users });
  return {
    ...modelBase,

    description,
    title,
    type,
    attachmentCounts: toAttachmentCounts(issueEventEntity.documents ?? []),
  };
}

function incomingEmailMessageEntityToModel(
  incomingEmailMessageEntity: IncomingEmailMessageEntity,
  users: UserEntity[]
): IncomingEmailMessageModel {
  const modelBase = entityToModelBase({
    entity: incomingEmailMessageEntity,
    users,
  });
  return {
    ...modelBase,
    subject: incomingEmailMessageEntity.subject,
    incomingEmailMessageContent:
      incomingEmailMessageEntity.incomingEmailMessageContent,
    attachmentCounts: toAttachmentCounts(incomingEmailMessageEntity.documents ?? []),
  };
}
